import React, { useState, useEffect, useCallback } from 'react';
import {
  SiteCreate,
  SiteUpdate,
  SiteDimension,
  StoreSiteType,
  StoreSiteSection,
  StoreHookupType,
  StoreOverageFee as StoreOverageFeeType,
  SiteHookup,
  Pricing,
  SiteOverageFee,
  SiteImposedCharge,
  SiteBlackout,
  SiteMedia
} from '../../../types/site';
import { SiteDetails } from '../../../types/siteData';
import { StorePricing, StoreTax, StoreCharge } from '../../../services/configService';
import { siteService } from '../../../services/siteService';
import { authService } from '../../../services/auth/authService';
import SiteBasicInfoForm from './SiteBasicInfoForm';
import SiteHookupSelector from './SiteHookupSelector';
import SitePricingFormV2 from './SitePricingFormV2';
import SiteOverageChargesForm from './SiteOverageChargesForm';
import SiteFeesFormV2 from './SiteFeesFormV2';
import SiteBlackoutsForm from './SiteBlackoutsForm';
import SiteMediaForm from './SiteMediaForm';
import './SiteManagement.css';

type TabId = 'basic' | 'hookups' | 'pricing' | 'overage-charges' | 'fees' | 'blackouts' | 'media';

interface Tab {
  id: TabId;
  label: string;
  disabled?: boolean;
}

interface SiteFormModalProps {
  siteId: number | null;
  siteTypes: StoreSiteType[];
  sections: StoreSiteSection[];
  hookupTypes: StoreHookupType[];
  storePricing: StorePricing[];
  storeTaxes: StoreTax[];
  storeCharges: StoreCharge[];
  storeOverageFees: StoreOverageFeeType[];
  onClose: () => void;
  onSave: () => void;
  initialActiveTab?: TabId;  // For deep linking from Sites Dashboard
  siteDetails?: SiteDetails; // Pre-loaded details from context cache
}

interface SiteFormData {
  alias: string;
  type_id: number | undefined;
  site_section_id: number | undefined;
  description: string;
  dimension: SiteDimension;
  max_length: number | undefined;
  seasonal: boolean;
}

const initialFormData: SiteFormData = {
  alias: '',
  type_id: undefined,
  site_section_id: undefined,
  description: '',
  dimension: {
    unit: 'feet',
    length: undefined,
    width: undefined,
    height: undefined
  },
  max_length: undefined,
  seasonal: false
};

const SiteFormModal: React.FC<SiteFormModalProps> = ({
  siteId,
  siteTypes,
  sections,
  hookupTypes,
  storePricing,
  storeTaxes,
  storeCharges,
  storeOverageFees,
  onClose,
  onSave,
  initialActiveTab,
  siteDetails
}) => {
  // Use initialActiveTab if provided and valid for editing mode
  const getInitialTab = (): TabId => {
    if (initialActiveTab && siteId !== null) {
      return initialActiveTab;
    }
    return 'basic';
  };
  const [activeTab, setActiveTab] = useState<TabId>(getInitialTab());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the current site ID (can change after creation)
  const [currentSiteId, setCurrentSiteId] = useState<number | null>(siteId);
  // Track if site was just created (to show "Done" button)
  const [justCreated, setJustCreated] = useState(false);

  // Form data
  const [formData, setFormData] = useState<SiteFormData>(initialFormData);

  // Related data (for editing)
  const [hookups, setHookups] = useState<SiteHookup[]>([]);
  const [pricing, setPricing] = useState<Pricing[]>([]);
  const [overageFees, setOverageFees] = useState<SiteOverageFee[]>([]);
  const [imposedCharges, setImposedCharges] = useState<SiteImposedCharge[]>([]);
  const [blackouts, setBlackouts] = useState<SiteBlackout[]>([]);
  const [media, setMedia] = useState<SiteMedia[]>([]);

  const isEditing = currentSiteId !== null;

  const tabs: Tab[] = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'hookups', label: 'Hookups', disabled: !isEditing },
    { id: 'pricing', label: 'Pricing', disabled: !isEditing },
    { id: 'overage-charges', label: 'Overage Charges', disabled: !isEditing },
    { id: 'fees', label: 'Fees', disabled: !isEditing },
    { id: 'blackouts', label: 'Blackouts', disabled: !isEditing },
    { id: 'media', label: 'Media', disabled: !isEditing }
  ];

  useEffect(() => {
    if (currentSiteId) {
      loadSiteData(currentSiteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSiteId]);

  const loadSiteData = async (id: number) => {
    try {
      setLoading(true);
      const siteWithDetails = await siteService.getSiteWithDetails(id);

      setFormData({
        alias: siteWithDetails.alias,
        type_id: siteWithDetails.type_id,
        site_section_id: siteWithDetails.site_section_id,
        description: siteWithDetails.description || '',
        dimension: siteWithDetails.dimension || { unit: 'feet' },
        max_length: siteWithDetails.max_length,
        seasonal: siteWithDetails.seasonal
      });

      // Use pre-loaded details from context cache if available,
      // otherwise use data from getSiteWithDetails
      if (siteDetails) {
        setHookups(siteDetails.hookups || []);
        setOverageFees(siteDetails.overageFees || []);
        setImposedCharges(siteDetails.charges || []);
        // Pricing still comes from getSiteWithDetails since context only loads it for list display
        setPricing(siteWithDetails.pricing || []);
      } else {
        setHookups(siteWithDetails.hookups || []);
        setPricing(siteWithDetails.pricing || []);
        setOverageFees(siteWithDetails.overage_fees || []);
        setImposedCharges(siteWithDetails.imposed_charges || []);
      }

      // Blackouts always from getSiteWithDetails (not cached in context)
      setBlackouts(siteWithDetails.blackouts || []);

      setError(null);
    } catch (err) {
      setError('Failed to load site data');
      console.error('Error loading site:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (updates: Partial<SiteFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleSave = async () => {
    if (!formData.alias.trim()) {
      setError('Site alias is required');
      setActiveTab('basic');
      return;
    }

    if (!formData.type_id) {
      setError('Site type is required');
      setActiveTab('basic');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const siteData: SiteCreate | SiteUpdate = {
        alias: formData.alias.trim(),
        type_id: formData.type_id,
        site_section_id: formData.site_section_id,
        description: formData.description.trim() || undefined,
        dimension: formData.dimension.length || formData.dimension.width ? formData.dimension : undefined,
        max_length: formData.max_length,
        seasonal: formData.seasonal
      };

      if (isEditing && currentSiteId) {
        // Update existing site
        await siteService.updateSite(currentSiteId, siteData);
        onSave();
      } else {
        // Create new site - keep modal open for additional configuration
        const created = await siteService.createSite(siteData as SiteCreate);
        setCurrentSiteId(created.id);
        setJustCreated(true);
        // Don't call onSave() - keep modal open for user to configure other tabs
      }
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to save site';
      setError(message);
      console.error('Error saving site:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDone = () => {
    // Close modal and refresh list
    onSave();
  };

  const handleHookupsChange = (updatedHookups: SiteHookup[]) => {
    setHookups(updatedHookups);
  };

  const handlePricingChange = (updatedPricing: Pricing[]) => {
    setPricing(updatedPricing);
  };

  const handleOverageFeesChange = (updatedFees: SiteOverageFee[]) => {
    setOverageFees(updatedFees);
  };

  const handleImposedChargesChange = (updatedCharges: SiteImposedCharge[]) => {
    setImposedCharges(updatedCharges);
  };

  const handleBlackoutsChange = (updatedBlackouts: SiteBlackout[]) => {
    setBlackouts(updatedBlackouts);
  };

  const handleMediaChange = useCallback((updatedMedia: SiteMedia[]) => {
    setMedia(updatedMedia);
  }, []);

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="modal-loading">
          <span>Loading site data...</span>
        </div>
      );
    }

    switch (activeTab) {
      case 'basic':
        return (
          <SiteBasicInfoForm
            formData={formData}
            siteTypes={siteTypes}
            sections={sections}
            onChange={handleFormChange}
          />
        );

      case 'hookups':
        return (
          <SiteHookupSelector
            siteId={currentSiteId!}
            hookups={hookups}
            hookupTypes={hookupTypes}
            onChange={handleHookupsChange}
          />
        );

      case 'pricing':
        return (
          <SitePricingFormV2
            siteId={currentSiteId!}
            siteTypeId={formData.type_id}
            pricing={pricing}
            storePricing={storePricing}
            storeTaxes={storeTaxes}
            onChange={handlePricingChange}
          />
        );

      case 'overage-charges':
        return (
          <SiteOverageChargesForm
            siteId={currentSiteId!}
            storeId={authService.getStoreId()!}
            overageFees={overageFees}
            storeOverageFees={storeOverageFees}
            onChange={handleOverageFeesChange}
          />
        );

      case 'fees':
        return (
          <SiteFeesFormV2
            siteId={currentSiteId!}
            imposedCharges={imposedCharges}
            storeCharges={storeCharges}
            onChange={handleImposedChargesChange}
          />
        );

      case 'blackouts':
        return (
          <SiteBlackoutsForm
            siteId={currentSiteId!}
            storeId={authService.getStoreId()!}
            blackouts={blackouts}
            onChange={handleBlackoutsChange}
          />
        );

      case 'media':
        return (
          <SiteMediaForm
            siteAlias={formData.alias}
            media={media}
            onChange={handleMediaChange}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="site-form-modal-overlay" onClick={onClose}>
      <div className="site-form-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="site-form-modal-header">
          <h2>{isEditing ? `Edit Site: ${formData.alias}` : 'Add New Site'}</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="site-form-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`site-form-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              disabled={tab.disabled}
              title={tab.disabled ? 'Save the site first to access this tab' : undefined}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Error Display */}
        {error && (
          <div className="form-error-banner">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Content */}
        <div className="site-form-content">
          {renderTabContent()}
        </div>

        {/* Footer */}
        <div className="site-form-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          {justCreated ? (
            <button
              className="btn btn-primary"
              onClick={handleDone}
            >
              Done
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Site'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteFormModal;
